import Image, { StaticImageData } from 'next/legacy/image'
// eslint-disable-next-line custom-rules/no-next-link
import Link from 'next/link'

import { useToggle } from 'react-use'

import Spinner from '@components/spinner'

import Arrow from '@images/layout/select-arrow.svg'

import styles from './select.module.scss'

type SelectProps<T> = {
  initialValue: T
  options: { key: T; name: string }[]
  className?: string
  icon?: StaticImageData
  onChange: (val: T) => void
  menuTop?: boolean
  isLoading?: boolean
}

type DialogProps<T> = {
  options: { key: T; name: string }[]
  callback: (val: T) => void
  isOpen: boolean
}

function Dialog<Type>({ options, callback, isOpen }: DialogProps<Type>): JSX.Element {
  return (
    // Open the dialog with css instead of JS in order to have in the initial render
    // the two links available in the DOM for crawler parsing
    <div className={`${styles.menu} ${isOpen ? styles['menu-open'] : ''}`}>
      {options.map((el, i) => (
        <div key={i} className={styles.item} onClick={(): void => callback(el.key)}>
          <Link href={`/${el.key}`} locale={false} legacyBehavior>
            <a onClick={(e): void => e.preventDefault()}>{el.name}</a>
          </Link>
        </div>
      ))}
    </div>
  )
}

export default function Select<Type>({
  initialValue,
  options,
  className = '',
  icon,
  onChange,
  menuTop = false,
  isLoading,
}: SelectProps<Type>): JSX.Element {
  const [isOpen, setIsOpen] = useToggle(false)

  const itemClick = (select: Type): void => {
    onChange(select)
    setIsOpen()
  }

  return (
    <div
      className={`
      ${styles['select-wrapper']}
      ${menuTop ? styles['top-menu'] : ''}
    `}
    >
      {menuTop && <Dialog options={options} callback={itemClick} isOpen={isOpen} />}
      <div className={`${styles.selector} ${className}`} tabIndex={0} onClick={(): void => setIsOpen()}>
        <div className={styles.selected}>
          {icon ? (
            <Image src={icon} className={styles.icon} layout="fixed" height={20} width={20} alt="Globe Icon" />
          ) : (
            <></>
          )}
          <p>{options.find((o) => o.key === initialValue)?.name}</p>
        </div>
        {isLoading ? (
          <Spinner scale="0.6" className={styles.loading} />
        ) : (
          <Image
            src={Arrow}
            layout="fixed"
            width={15}
            height={15}
            className={`${isOpen ? styles.rotate : ''} ${styles.img}`}
            alt="arrow"
          />
        )}
      </div>
      {!menuTop && <Dialog options={options} callback={itemClick} isOpen={isOpen} />}
    </div>
  )
}
